import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons'; // npm install react-bootstrap-icons
import { signup } from '../services/AuthService';
import { validateEmail, validatePassword, validateUsername } from '../services/Validation';  // Import des fonctions de validation
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

import '../css/forms.css';

const SignupModal = ({ show, handleClose }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  
  const usernameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (show) {
      setUsername('');  // Réinitialiser le champ username
      setPassword('');  // Réinitialiser le champ password
      setEmail('');  // Réinitialiser le champ password
      setMessage('');   // Réinitialiser le message
      setErrors({});    // Réinitialiser les erreurs
      setTimeout(() => {
        if (usernameRef.current) {
          usernameRef.current.focus();
        }
      }, 100); 
    }
  }, [show]);

  const handleSignup = async () => {
    // Valider les champs
    const usernameValidation = validateUsername(username);
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    // Si une des validations échoue, on met à jour les erreurs et on arrête l'inscription
    if (!usernameValidation.valid || !emailValidation.valid || !passwordValidation.valid) {
      setErrors({
        username: usernameValidation.error,
        email: emailValidation.error,
        password: passwordValidation.error,
      });
      return;
    }

    // Réinitialiser les erreurs si tout est valide
    setErrors({});

    try {
      const userLanguage = i18n.language || 'en';

      const responseMessage = await signup(username, email, password, userLanguage); // Appelle l'API d'inscription
      setMessage(responseMessage || "Un email de validation a été envoyé à votre adresse."); // Message de confirmation
      
      setUsername(''); // Réinitialiser les champs du formulaire
      setEmail('');
      setPassword('');     
      setTimeout(() => {
        handleClose();
        navigate('/');
      }, 2000);
    } catch (error) {
      setMessage(error.message || "Erreur lors de l'inscription.");
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header>
        <Modal.Title>{t('modal.account.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="formUsername">
            <Form.Label>{t('modal.account.username.value')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('modal.account.username.default')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              isInvalid={!!errors.username}
              ref={usernameRef}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (emailRef.current) emailRef.current.focus();
                }
              }}
            />
            <Form.Control.Feedback type="invalid">
              {errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formEmail">
            <Form.Label>{t('modal.account.email.value')}</Form.Label>
            <Form.Control
              type="email"
              placeholder={t('modal.account.email.default')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isInvalid={!!errors.email}
              ref={emailRef}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (passwordRef.current) passwordRef.current.focus();
                }
              }}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formPassword">
            <Form.Label>{t('modal.account.password.value')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('modal.account.password.default')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isInvalid={!!errors.password}
                  ref={passwordRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSignup();
                    }
                  }}
                />
                <Button
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </InputGroup>
          </Form.Group>

          <Button variant="primary" onClick={handleSignup}>
            {t('modal.account.subscribe')}
          </Button>
        </Form>
        {message && (
              <p
                style={{
                  marginTop: '10px',
                  color: '#ff4d4f',        // rouge vif pour l’erreur
                  fontWeight: 'bold',      // un peu plus visible
                  padding: '8px 12px',
                  borderRadius: '5px',
                }}
              >
                {message}
              </p>
            )}
      </Modal.Body>
    </Modal>
  );
};

export default SignupModal;
